import {
  type RefObject,
  type Dispatch,
  type SetStateAction,
  forwardRef,
  memo,
  useEffect,
  useState,
} from "react";
import useAutocomplete from "../../hooks/useAutocomplete";
import MenuList from "../../../Form/components/SelectField/components/MenuList";
import Select, {
  type ActionMeta,
  type InputActionMeta,
  type SelectInstance,
} from "react-select";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import Icon from "../../../Icon/Icon";
import { IconColorE, LangE } from "../../../../models/enums";
import useQueryParams from "../../../../utils/hooks/useQueryParams";
import { useAppSelector } from "../../../../store/hooks";
import { selectCurrentLang } from "../../../../store/selectors/appSelectors";
import { clss } from "../../../../utils/helpers/styleHelpers";
import { isMobileOrTablet } from "../../../../utils/helpers/mediaQueries";

import type { SelectI, SelectOptionT } from "../../../../models/models";

import styles from "./styles.module.scss";

interface AutocompleteI {
  isForCampaignsPage: boolean;
  autocompleteCategory: SelectOptionT;
  setShowSearchBar?: Dispatch<SetStateAction<boolean>>;
  /**
   * True once the user has focused or clicked anywhere in the search area. Gates the autocomplete's
   * lookup-list downloads so a cold page load does not pay for them — see useAutocomplete. Spec 34 D1.
   */
  isActive?: boolean;
}

const FILTER_TYPE_QUERY_PARAM_NAME = "filterType";
const FILTER_LABEL_QUERY_PARAM_NAME = "filterLabel";

const customComponents = {
  MenuList,
  DropdownIndicator: null,
};

// --------------------- STYLES ----------------------- START

const SEARCH_BAR_HEIGHT = 39;
const PADDING_FOR_X_ICON = 40;
const FONT_SIZE = 14;
const BORDER_COLOR = "#E4E6E7";

const activeStyles = {
  borderColor: BORDER_COLOR,
  outline: "none",
  boxShadow: "none",
};

const selectStylesOverriding = (lang: LangE, isMobile: boolean) => ({
  ...(isMobile && {
    menu: (provided: object) => ({
      ...provided,
      zIndex: 99999,
    }),
    menuPortal: (provided: object) => ({
      ...provided,
      zIndex: 99999,
    }),
  }),
  // react-select sizes its text input to the typed value: the <input> sits in a
  // `min-content` grid column whose width comes from an ::after mirror of the
  // value. Once the query outgrows the control that column keeps growing, and
  // the control's `overflow: hidden` silently cuts the rest of the text off -
  // together with the caret, which ends up outside the field. Drop the mirror
  // and let the input fill the control instead, so a long query scrolls inside
  // the field the way a plain text input does.
  input: (provided) => ({
    ...provided,
    gridTemplateColumns: "0 minmax(0, 1fr)",
    "&:after": { display: "none" },
  }),
  control: (provided) => ({
    ...provided,
    overflow: "hidden",
    height: SEARCH_BAR_HEIGHT,
    borderRadius: 0,
    borderInlineStartWidth: 0,
    borderInlineEndWidth: 0,
    borderColor: BORDER_COLOR,
    paddingInlineEnd: PADDING_FOR_X_ICON,
    boxShadow: "none",
    fontSize: FONT_SIZE,
    "&:hover": activeStyles,
    "&:active": activeStyles,
    "&:focus": activeStyles,
  }),
  option: (provided) => ({
    ...provided,
    fontSize: FONT_SIZE,
    overflow: "hidden",
    direction: lang === LangE.HE ? "rtl" : "ltr",
  }),
});

// --------------------- STYLES ----------------------- END

const AutocompleteInput = memo(
  forwardRef((props: AutocompleteI, ref) => {
    const {
      isForCampaignsPage,
      autocompleteCategory,
      setShowSearchBar,
      isActive,
    } = props;

    const { t } = useTranslate();
    const lang = useAppSelector(selectCurrentLang);
    const { searchParams } = useQueryParams();
    const [inputValue, setInputValue] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [
      autocompleteOptions,
      setAutocompleteOptions,
      selectedAutocomplete,
      setSelectedAutocomplete,
    ] = useAutocomplete(isActive);

    useEffect(() => {
      // set default values from query params
      if (!inputValue) {
        const searchTextQueryParam = searchParams.get(
          FILTER_LABEL_QUERY_PARAM_NAME
        );

        // set filter text value if the query param exists
        if (searchTextQueryParam) {
          const value = searchTextQueryParam.split(":")[1].trim();
          setInputValue(value);
          // if we have selected value, react-select library won't show input value for some reason,
          // so we need to clear selected value if we want to show input data
          // I'm doing it everywhere in the same cases
          setSelectedAutocomplete(null);
        }
      }
    }, []);

    // reset input on category changed and set new autocomplete options
    useEffect(() => {
      setAutocompleteOptions(autocompleteCategory);
      const categoryIsNotTheSameAsInURLCategoryQueryParam =
        autocompleteCategory?.value &&
        searchParams.get(FILTER_TYPE_QUERY_PARAM_NAME) !==
          autocompleteCategory?.value;

      if (categoryIsNotTheSameAsInURLCategoryQueryParam) {
        setSelectedAutocomplete(null);
        setInputValue("");
      }
    }, [autocompleteCategory?.value]);

    const handleSelect = (newValue, { action }: ActionMeta<unknown>) => {
      if (action === "select-option") {
        setInputValue("");
        setSelectedAutocomplete(newValue);
      }
    };

    const handleInputChange = (
      newValue: string,
      { action }: InputActionMeta
    ) => {
      if (action === "input-change") {
        setInputValue(newValue);
      }
    };

    const handleClear = () => {
      if (!inputValue && !selectedAutocomplete) {
        setShowSearchBar?.(false);
      }

      setInputValue("");
      setSelectedAutocomplete(null);
    };

    return (
      <div>
        <Select
          ref={ref as RefObject<SelectInstance<SelectI>>}
          menuPortalTarget={isMobileOrTablet() ? document.body : undefined}
          onFocus={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
          options={autocompleteOptions}
          value={selectedAutocomplete}
          onChange={handleSelect}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          className={clss([
            styles.searchInput,
            isEditing ? styles.editingSearchInput : "",
          ])}
          controlShouldRenderValue
          menuIsOpen={
            (autocompleteOptions?.length && inputValue) ||
            // show dropdown for "production location" category even if there is no value in input
            autocompleteCategory?.value === "productionlocation"
              ? undefined
              : false
          }
          isRtl={lang === LangE.HE}
          components={customComponents}
          noOptionsMessage={() => null}
          placeholder={
            isForCampaignsPage
              ? t`Enter request to search for campaigns`
              : t`Enter request to search for deliveries`
          }
          styles={selectStylesOverriding(lang, isMobileOrTablet())}
        />
        {(!!inputValue ||
          !!selectedAutocomplete?.label ||
          isMobileOrTablet()) && (
          <div
            className={styles.clearIconWrapper}
            onClick={handleClear}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleClear()}
            aria-label="Clear"
          >
            <Icon
              name="close"
              className={styles.clearIcon}
              color={IconColorE.GRAY}
            />
          </div>
        )}
      </div>
    );
  })
);

export default AutocompleteInput;
